import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { Locale } from '@/shared/i18n/locales';
import { MOBILE_QUERY } from '@/shared/mobile/use-media-query';

export type ThemePreference = 'light' | 'dark' | 'system';

/** Largura do painel do Luy (D-L03): padrão e limites do divisor, em px. 420 → 460 em 06/10/2026 (Leon). */
export const LUY_WIDTH = { default: 460, min: 360, max: 640 } as const;

/** Padrão anterior: guardado sem a pessoa ter mexido (a store gravava sempre), então não é escolha. */
const LUY_WIDTH_V0_DEFAULT = 420;

export const clampLuyWidth = (width: number) => Math.round(Math.min(LUY_WIDTH.max, Math.max(LUY_WIDTH.min, width)));

interface ShellState {
  theme: ThemePreference;
  sidebarCollapsed: boolean;
  tenantId: string | undefined;
  luyOpen: boolean;
  luyWidth: number;
  luyExpanded: boolean;
  /** O painel recolheu a sidebar ao abrir (D-L02): ao fechar, ela volta aberta. */
  luyCollapsedSidebar: boolean;
  setTheme: (theme: ThemePreference) => void;
  toggleSidebar: () => void;
  setTenant: (tenantId: string | undefined) => void;
  /** `split`: o painel divide a tela (≥ 1280 px) e, por isso, recolhe a sidebar. */
  openLuy: (options: { split: boolean }) => void;
  closeLuy: () => void;
  /** A janela cruzou os 1280 px com o painel aberto: recolhe a sidebar ao dividir e devolve ao cobrir (D-L02). */
  syncLuySplit: (split: boolean) => void;
  setLuyWidth: (width: number) => void;
  toggleLuyExpanded: () => void;
}

export function applyTheme(pref: ThemePreference) {
  const dark = pref === 'dark' || (pref === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.dataset['theme'] = dark ? 'dark' : 'light';
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
  try {
    localStorage.setItem('uyzy.theme', pref);
  } catch {
    /* modo privado: o tema vale só nesta aba */
  }
}

export const useShell = create<ShellState>()(
  persist(
    (set) => ({
      theme: 'system',
      sidebarCollapsed: false,
      tenantId: undefined,
      setTenant: (tenantId) => set({ tenantId }),
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
      luyOpen: false,
      luyWidth: LUY_WIDTH.default,
      luyExpanded: false,
      luyCollapsedSidebar: false,
      // Mexer na sidebar com o painel aberto é escolha da pessoa: o fechamento não desfaz.
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed, luyCollapsedSidebar: false })),
      openLuy: ({ split }) =>
        set((s) => {
          if (s.luyOpen) return {};
          const collapse = split && !s.sidebarCollapsed;
          return { luyOpen: true, ...(collapse ? { sidebarCollapsed: true, luyCollapsedSidebar: true } : {}) };
        }),
      closeLuy: () =>
        set((s) => ({
          luyOpen: false,
          luyExpanded: false,
          luyCollapsedSidebar: false,
          ...(s.luyCollapsedSidebar ? { sidebarCollapsed: false } : {}),
        })),
      syncLuySplit: (split) =>
        set((s) => {
          if (!s.luyOpen) return {};
          if (split) return s.sidebarCollapsed ? {} : { sidebarCollapsed: true, luyCollapsedSidebar: true };
          return s.luyCollapsedSidebar ? { sidebarCollapsed: false, luyCollapsedSidebar: false } : {};
        }),
      setLuyWidth: (width) => set({ luyWidth: clampLuyWidth(width) }),
      toggleLuyExpanded: () => set((s) => ({ luyExpanded: !s.luyExpanded })),
    }),
    {
      name: 'uyzy.shell',
      storage: createJSONStorage(() => localStorage),
      version: 1,
      // v1: quem nunca mexeu na largura tinha 420 gravado e passa ao padrão novo; quem escolheu mantém.
      migrate: (persisted, version) => {
        const saved = { ...(persisted as Partial<ShellState> | undefined) };
        if (version < 1 && saved.luyWidth === LUY_WIDTH_V0_DEFAULT) delete saved.luyWidth;
        return saved;
      },
      // Largura só vai para o armazenamento quando é escolha (diferente do padrão): mudar o padrão alcança todos os outros.
      partialize: (s) => ({
        theme: s.theme,
        sidebarCollapsed: s.sidebarCollapsed,
        tenantId: s.tenantId,
        luyOpen: s.luyOpen,
        ...(s.luyWidth === LUY_WIDTH.default ? {} : { luyWidth: s.luyWidth }),
        luyCollapsedSidebar: s.luyCollapsedSidebar,
      }),
      merge: (persisted, current) => {
        const saved = (persisted ?? {}) as Partial<ShellState>;
        const next = { ...current, ...saved, luyWidth: clampLuyWidth(Number.isFinite(saved.luyWidth) ? Number(saved.luyWidth) : current.luyWidth) };
        // No iPhone o painel é tela cheia: abrir o PWA não pode cair dentro do Luy (revisão da L2a).
        if (!next.luyOpen || !window.matchMedia(MOBILE_QUERY).matches) return next;
        return { ...next, luyOpen: false, luyCollapsedSidebar: false, sidebarCollapsed: next.luyCollapsedSidebar ? false : next.sidebarCollapsed };
      },
    },
  ),
);

export function persistLocale(locale: Locale) {
  try {
    localStorage.setItem('uyzy.locale', locale);
  } catch {
    /* sem armazenamento: vale só nesta aba */
  }
}
